import trimEnd from 'lodash-es/trimEnd'
import fetch from 'node-fetch'

import { IFastApiProvider } from './interface'

import {
  ILogger, IBTCUnspent,
  NetworkError, RPCNode, ChainType,
} from '@one-chain/core'

const CHAIN_TYPE_MAP = {
  [ChainType.BTC]: 'btc',
}

const CHAIN_ID_MAP = {
  'mainnet': 'main',
  'testnet': 'test3',
}

/**
 * BlockCypher
 *
 * WARNING 仅限开发调试用，传输的数据可能存在精度问题。
 */
export class BlockCypher implements IFastApiProvider {
  protected logger: ILogger
  protected endpoint: string

  readonly supportBatch = true
  readonly rateLimit = 3

  static instance: BlockCypher

  constructor (rpcnode: RPCNode, logger: ILogger) {
    if (BlockCypher.instance) {
      return BlockCypher.instance
    }

    this.logger = logger
    this.endpoint = `${trimEnd(rpcnode.baseUrl, '/')}/${CHAIN_TYPE_MAP[rpcnode.chainType]}/${CHAIN_ID_MAP[rpcnode.chainId]}`

    BlockCypher.instance = this
  }

  async ping () {
    return this.get('/')
  }

  async getAddresses (addresses: string[]) {
    let results = await this.get(`/addrs/${addresses.join(';')}`, { limit: 1 })
    // 单地址的情况下返回值不是数组，那就转为数组
    if (addresses.length <= 1) {
      results = [results]
    }
    return results.map(val => {
      return {
        address: val.address,
        balance: val.balance.toString(),
        txCount: val.n_tx,
      }
    })
  }

  async getUnspentOfAddresses (addresses: string[], onlyConfirmed = false) {
    let results = await this.get(`/addrs/${addresses.join(';')}`, { unspentOnly: true })
    // 单地址的情况下返回值不是数组，那就转为数组
    if (addresses.length <= 1) {
      results = [results]
    }

    const unspents: IBTCUnspent[] = []
    results.forEach(val => {
      for (const tx of val.txrefs) {
        // Skip unconfirmed unspent if only choose confirmed unspent.
        if (onlyConfirmed && !tx.confirmed) {
          continue
        }

        unspents.push({
          txId: tx.tx_hash,
          address: val.address,
          vout: tx.tx_output_n,
          value: tx.value,
        })
      }
    })

    return unspents
  }

  async pushTransaction (rawTransaction: string): Promise<string> {
    // @ts-ignore
    // eslint-disable-next-line
    const result = await this.post(`/txs/push`, { tx: rawTransaction }, { token: BLOCKCYPHER_TOKEN })
    return result.hash
  }

  protected buildUrl (path: string, query: any = null) {
    let url = this.endpoint + path

    if (query) {
      url += '?' + new URLSearchParams(query)
    }

    return url
  }

  protected async get (path, query: any = null): Promise<any> {
    let ret
    try {
      const url = this.buildUrl(path, query)
      !PROD && this.logger.trace('BTC - Request data: ', url)
      const res = await fetch(url, {
        method: 'GET',
        // @ts-ignore
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        },
      })

      if (res.ok) {
        ret = await res.text()
        if (ret) {
          ret = JSON.parse(ret)
        }
        !PROD && this.logger.trace('BTC - Response data: ', ret)
      }
      else {
        throw NetworkError.fromCode(res.status, `${res.status} ${res.statusText}`)
      }
    }
    catch (err) {
      if (err instanceof NetworkError) {
        throw err
      }
      else {
        throw NetworkError.fromCode(0, err.message)
      }
    }

    return ret
  }

  protected async post (path, data, query: any = null): Promise<any> {
    let ret
    try {
      const url = this.buildUrl(path, query)
      !PROD && this.logger.trace('BTC - Request data: ', url, data)
      const res = await fetch(url, {
        method: 'POST',
        // @ts-ignore
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      if (res.ok) {
        ret = await res.text()
        if (ret) {
          ret = JSON.parse(ret)
        }
        !PROD && this.logger.trace('BTC - Response data: ', ret)
      }
      else {
        throw NetworkError.fromCode(res.status, `${res.status} ${res.statusText}`)
      }
    }
    catch (err) {
      if (err instanceof NetworkError) {
        throw err
      }
      else {
        throw NetworkError.fromCode(0, err.message)
      }
    }

    return ret
  }
}

export default BlockCypher
